<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { email, required } from '@vuelidate/validators'
import { rails_url } from '@/modules/shared/utils/rails'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { VButton, VLoadingSection, VTaxIdField, VTextField } from '@/modules/shared/components'
import ProfileImage from '../profile-image.vue'
import VSelect from '@/modules/investing/components/v-select.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useAddressStore } from '@/modules/shared/stores/address-store'

const props = defineProps<{
  individual: any
  inDisplayMode?: boolean
  isWizard?: boolean
  onSubmit: (payload: any) => Promise<void>
}>()

const authStore = useAuthStore()
const addressStore = useAddressStore()

const isWizard = computed(() => !!props.isWizard)
const isFieldDisabled = computed(() => !!props.inDisplayMode)
const currentIndividual = computed(() => props.individual)
const { skeleton: internal_skeleton, hideSkeleton, showSkeleton } = useSkeleton()
const skeleton = computed(() => !currentIndividual.value || internal_skeleton.value)

///////////////////////////////////////////////////////////////////////////////
// Form
///////////////////////////////////////////////////////////////////////////////

const individual_initial_state = {
  birthdate: '',
  citizenship: '',
  city: '',
  country: '',
  email: '',
  first_name: '',
  last_name: '',
  investing_name: '',
  phone: '',
  profile_picture: null,
  replace_profile_picture: false,
  state: '',
  street1: '',
  street2: '',
  tax_id: '',
  zip: '',
}

const NON_US_INVESTOR_TAX_ID_VALUE = 'NON-US INVESTOR'
const is_non_us_investor = ref(false)

const individual_rules = {
  citizenship: {
    required: {
      $validator: (value) => {
        if (!is_non_us_investor.value) return true
        return !!value
      },
      $message: 'Value is required',
    },
  },
  email: { required, email },
  first_name: { required },
  tax_id: {
    hasNineDigits: {
      $validator: (value) => {
        if (!value || is_non_us_investor.value) return true
        const digits = value.replace(/\D/g, '')
        return digits.length === 9
      },
      $message: 'Tax ID must contain exactly 9 digits',
    },
  },
}
const individual_form = ref({ ...individual_initial_state })
const v$ = useVuelidate(individual_rules, individual_form, { $lazy: true })

const setForm = () => {
  imageToDisplay.value = currentIndividual.value.profile_picture
  is_non_us_investor.value = currentIndividual.value.tax_id === NON_US_INVESTOR_TAX_ID_VALUE

  individual_form.value.birthdate = currentIndividual.value.birthdate
  individual_form.value.citizenship = currentIndividual.value.citizenship
  individual_form.value.email = currentIndividual.value.email
  individual_form.value.first_name = currentIndividual.value.first_name
  individual_form.value.last_name = currentIndividual.value.last_name
  individual_form.value.investing_name = currentIndividual.value.investing_name
  individual_form.value.phone = currentIndividual.value.phone
  individual_form.value.tax_id = currentIndividual.value.tax_id
  individual_form.value.street1 = currentIndividual.value.address.line1
  individual_form.value.street2 = currentIndividual.value.address.line2
  individual_form.value.city = currentIndividual.value.address.city
  individual_form.value.state = currentIndividual.value.address.state
  individual_form.value.zip = currentIndividual.value.address.postal_code
  individual_form.value.country = currentIndividual.value.address.country
}

const resetForm = () => {
  v$.value.$reset()
  setForm()
}

watch(currentIndividual, (value) => {
  if (value) setForm()
})

const isValid = async () => {
  const valid = await v$.value.$validate()
  return valid
}

const submit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  const payload = { ...individual_form.value }
  await props.onSubmit(payload)
}

defineExpose({
  isValid,
  resetForm,
  submit,
})

///////////////////////////////////////////////////////////////////////////////
// Profile Pic
///////////////////////////////////////////////////////////////////////////////

const imageToDisplay = ref(null)

const changeProfileImage = (image: File) => {
  individual_form.value.profile_picture = image
  individual_form.value.replace_profile_picture = true

  // read image to display
  const reader = new FileReader()
  reader.onload = (e) => {
    imageToDisplay.value = e.target.result
  }
  reader.readAsDataURL(image)
}

const removeProfileImage = () => {
  imageToDisplay.value = null
  individual_form.value.profile_picture = null
  individual_form.value.replace_profile_picture = true
}

///////////////////////////////////////////////////////////////////////////////
// Tax
///////////////////////////////////////////////////////////////////////////////

const toggleIsNonUsInvestor = () => {
  is_non_us_investor.value = !is_non_us_investor.value
}

watch(is_non_us_investor, () => {
  if (is_non_us_investor.value) {
    individual_form.value.tax_id = NON_US_INVESTOR_TAX_ID_VALUE
  } else {
    individual_form.value.tax_id = ''
    individual_form.value.citizenship = ''
  }
})

watch(
  () => individual_form.value.country,
  async (countryCode, prevCountry) => {
    if (!countryCode) return
    await addressStore.fetchStates(countryCode)

    // Only reset when the user switches countries after load
    if (prevCountry && countryCode !== prevCountry) {
      individual_form.value.state = null
    }
  },
  { immediate: true },
)

onMounted(async () => {
  showSkeleton()
  await addressStore.fetchCountries()
  hideSkeleton()
})
</script>

<template>
  <div class="w-full max-w-full rounded-lg border-[1px] p-3 sm:p-5">
    <div class="mb-5 flex items-center justify-between">
      <div class="text-lg font-semibold">Personal Information</div>
      <div class="flex items-center gap-3">
        <slot name="actions"></slot>
      </div>
    </div>
    <div class="space-y-6" v-if="!skeleton">
      <div class="mb-12 w-fit">
        <ProfileImage
          :profile_picture="imageToDisplay"
          :accept="['.png', '.jpg', '.jpeg']"
          :is_image_new="!currentIndividual?.profile_picture"
          :initials="currentIndividual?.initials"
          :onUpload="changeProfileImage"
          :onRemove="removeProfileImage"
          :disabled="isFieldDisabled"
        />
      </div>
      <div class="flex flex-col gap-6 sm:flex-row">
        <VTextField
          v-model="individual_form.first_name"
          label="First Name"
          property="first_name"
          type="text"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
        <VTextField
          v-model="individual_form.last_name"
          label="Last Name"
          property="last_name"
          type="text"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
      </div>
      <div class="flex flex-col gap-6 sm:flex-row">
        <VTextField
          v-model="individual_form.birthdate"
          label="Birthdate"
          property="birthdate"
          type="date"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
      </div>
      <VTextField
        v-if="authStore.is_site_admin"
        v-model="individual_form.investing_name"
        label="Investing Name (Site Admin Only)"
        property="investing_name"
        type="text"
        :v$="v$"
        :required="true"
        :disabled="isFieldDisabled"
        disabledVariant="light"
      />
      <div class="flex flex-col gap-6 sm:flex-row">
        <VTextField
          v-model="individual_form.email"
          label="Email"
          property="email"
          type="email"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
        <VTextField
          v-model="individual_form.phone"
          label="Phone"
          property="phone"
          type="text"
          :v$="v$"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
      </div>
      <div class="flex flex-col gap-6 sm:flex-row">
        <div class="w-full sm:w-1/2">
          <VTextField
            v-if="is_non_us_investor"
            v-model="individual_form.tax_id"
            label="SSN / Tax ID"
            :disabled="true"
            property="tax_id"
            type="text"
            :v$="v$"
            :required="true"
            disabledVariant="light"
          />
          <VTaxIdField
            v-else
            v-model="individual_form.tax_id"
            label="SSN / Tax ID"
            format="ssn"
            :disabled="isFieldDisabled"
            property="tax_id"
            :v$="v$"
            :required="true"
            description="SSN / Tax ID is an encrypted attribute"
            disabledVariant="light"
          />
          <div class="mt-2 flex items-center gap-3" v-if="!isFieldDisabled">
            <input type="checkbox" v-model="is_non_us_investor" />
            <p class="cursor-pointer text-sm font-medium text-gray-700" @click="toggleIsNonUsInvestor">
              Non-US investors, please check this box
            </p>
          </div>
          <div
            class="mt-2 border-[1px] bg-[#3982AF]/10 p-2 text-sm text-[#3982AF] ring-[#3982AF]/20"
            v-if="is_non_us_investor && !isFieldDisabled"
          >
            Please complete the
            <a href="https://www.irs.gov/pub/irs-pdf/fw8ben.pdf" target="_blank" class="hyperlink !font-semibold"
              >W-8BEN form</a
            >
            and upload it
            <a :href="`${rails_url()}/investors/${currentIndividual.id}/files`" class="hyperlink !font-semibold">here</a
            >. If you have already done this, please disregard this message.
          </div>
        </div>
        <VSelect
          v-if="is_non_us_investor"
          v-model="individual_form.citizenship"
          label="Citizenship"
          :options="addressStore.countryOptions"
          property="citizenship"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
      </div>

      <hr class="my-12 h-1 border-0 bg-gray-100" />

      <div class="mb-5 text-lg font-semibold">Residential Address</div>
      <VTextField
        v-model="individual_form.street1"
        label="Street Address 1"
        property="street1"
        type="text"
        :v$="v$"
        :required="true"
        :disabled="isFieldDisabled"
        disabledVariant="light"
      />
      <VTextField
        v-model="individual_form.street2"
        label="Street Address 2 (Optional)"
        property="street1"
        type="text"
        :v$="v$"
        :required="true"
        :disabled="isFieldDisabled"
        disabledVariant="light"
      />
      <div class="flex flex-col gap-6 sm:flex-row">
        <VSelect
          v-model="individual_form.country"
          label="Country"
          :options="addressStore.countryOptions"
          property="country"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />

        <VTextField
          v-model="individual_form.city"
          label="City"
          property="city"
          type="text"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
      </div>
      <div class="flex flex-col gap-6 sm:flex-row">
        <VSelect
          v-model="individual_form.state"
          label="State / Province"
          :options="addressStore.stateOptions"
          property="state"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />

        <VTextField
          v-model="individual_form.zip"
          label="Zip / Postal Code"
          property="zip"
          type="text"
          :v$="v$"
          :required="true"
          class="w-full sm:w-1/2"
          :disabled="isFieldDisabled"
          disabledVariant="light"
        />
      </div>
    </div>
    <VLoadingSection
      v-else
      class="my-40"
      title="Preparing Your Information"
      description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs
    smoothly."
    />
  </div>
  <div class="mt-24" v-if="!isFieldDisabled && !isWizard">
    <VButton variant="v-blue" size="lg" :click="submit">Confirm Changes</VButton>
  </div>
</template>
